import { DateTime } from 'luxon'
import { DateString } from '§C/types/system.type'

export const SECONDS_PER_DAY = 86400

export function toUnix(date: DateTime) {
  return date.toUnixInteger()
}

export function fromUnix(unix: number) {
  return DateTime.fromSeconds(unix, { zone: 'utc' })
}

/** How long ago a unix time was, e.g. "14 days ago" */
export function fromUnixRelative(unix: number): string {
  return DateTime.fromSeconds(unix, { locale: 'en' }).toRelative() ?? ''
}

/** A moment something happened, in the user's own time zone, e.g. "Oct 26, 2026, 14:05" */
export function toDisplayDateTimeString(unix: number): string {
  return DateTime.fromSeconds(unix).toLocaleString(DateTime.DATETIME_MED)
}

export function unixTimeStampNow(): number {
  return DateTime.now().toUnixInteger()
}

export function fromDateString(dateString: DateString) {
  return DateTime.fromISO(dateString, { zone: 'utc' })
}

export function toDateString(date: DateTime) {
  return date.toISODate()!
}

export function fromDateStringToUnix(dateString: DateString) {
  return fromDateString(dateString).toUnixInteger()
}

/**
 * Returns today's local wall-clock date as a UTC DateTime at midnight.
 * E.g. if local time is 00:01 on Feb 23 (CET), returns UTC Feb 23T00:00:00Z.
 */
export function todayAsUtc(): DateTime {
  const now = DateTime.now()
  return DateTime.utc(now.year, now.month, now.day)
}

export function todayAsUtcUnix(): number {
  return todayAsUtc().toUnixInteger()
}

/**
 * Normalizes an ISO timestamp to UTC midnight, preserving the wall-clock date.
 * Logs a warning if the timestamp contains a non-UTC timezone offset.
 */
/**
 * Formats a DateTime or ISO date string to a human-readable display string.
 * E.g. "Jan 15, 2026" or with weekday: "Thu, Jan 15, 2026"
 */
export function toDisplayDateString(date: DateTime | string | number, { weekday = false, year = true } = {}): string {
  const dt = typeof date === 'number' ? fromUnix(date)
    : typeof date === 'string' ? fromDateString(date)
    : date
  if (!dt.isValid) return typeof date === 'string' ? date : ''
  return dt.toLocaleString({
    ...(weekday && { weekday: 'short' }),
    month: 'short',
    day: 'numeric',
    ...(year && { year: 'numeric' })
  })
}

/**
 * Formats a half-open unix range [start, end) as the days it covers, inclusive.
 * E.g. "Jan 15, 2026 – Jan 17, 2026 (3 days)" or "Jan 15, 2026 (1 day)"
 */
export function formatCoveredDays(startUnix: number, endUnixExclusive: number): string {
  const days = Math.round((endUnixExclusive - startUnix) / SECONDS_PER_DAY)
  const firstDay = toDisplayDateString(startUnix)
  if (days === 1) return `${firstDay} (1 day)`
  return `${firstDay} – ${toDisplayDateString(endUnixExclusive - SECONDS_PER_DAY)} (${days} days)`
}

// Whether the half-open unix ranges [start1, end1) and [start2, end2) intersect.
export function timestampsOverlap(start1: number, end1: number, start2: number, end2: number): boolean {
  return start1 < end2 && start2 < end1
}

/**
 * An IANA time zone as people read it, with its current offset: "Europe/Oslo" ->
 * "Oslo (UTC+2)", "Asia/Kolkata" -> "Kolkata (UTC+5:30)". The offset follows daylight
 * saving at `at`, so a winter label reads UTC+1 for Oslo.
 */
export function timeZoneLabel(zone: string, at: DateTime = DateTime.now()): string {
  const offset = at.setZone(zone).offset
  const sign = offset < 0 ? '-' : '+'
  const hours = Math.floor(Math.abs(offset) / 60)
  const minutes = Math.abs(offset) % 60
  const city = zone.split('/').pop()!.replaceAll('_', ' ')
  return `${city} (UTC${offset === 0 ? '' : `${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`})`
}

export function normalizeToUtcDate(isoString: string): string {
  const dt = DateTime.fromISO(isoString, { setZone: true, zone: 'utc' })
  if (!dt.isValid) return isoString

  if (dt.offset !== 0) {
    const normalized = DateTime.utc(dt.year, dt.month, dt.day).toISO()!
    console.warn(`[Axios] Event timestamp "${isoString}" has non-UTC offset. Normalized to "${normalized}".`)
    return normalized
  }

  return DateTime.utc(dt.year, dt.month, dt.day).toISO()!
}
